import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import { statusLabel, statusTone } from "components/markets/marketActions";
import { PeriodPicker, Tile, TileSkeleton, bpsLabel, periodRange } from "components/revenue/RevenueStatement";
import { apiGet, buildQueryString } from "lib/apiClient";
import { formatKes } from "lib/format";

export const APPROVAL_ACTION_META = {
  SUBMITTED: { label: "Submitted for approval", tone: "blue" },
  APPROVED: { label: "Approved", tone: "green" },
  REJECTED: { label: "Sent back with changes requested", tone: "red" },
  PUBLISHED: { label: "Published", tone: "green" },
  UNPUBLISHED: { label: "Unpublished", tone: "gray" },
  RESULT_PROPOSED: { label: "Result proposed", tone: "amber" },
  RESULT_CONFIRMED: { label: "Result confirmed", tone: "green" },
  RESULT_REJECTED: { label: "Result rejected", tone: "red" },
  RESULT_DISPUTED: { label: "Result disputed", tone: "red" },
};

const ACTIVITY_LABEL = {
  PARTNER_REPORT_VIEWED: "Viewed their performance report",
  PARTNER_REPORT_EXPORTED: "Exported their performance report",
  PARTNER_REPORT_VIEWED_BY_TENANT: "Report viewed by your team",
  PORTAL_MARKET_CREATED: "Created a market",
  PORTAL_MARKET_DRAFT_STARTED: "Started a market draft",
  PORTAL_MARKET_EDITED: "Edited a market",
  PORTAL_MARKET_SUBMIT_FOR_APPROVAL: "Submitted a market for approval",
  PORTAL_MARKET_SUSPEND: "Suspended a market",
  PORTAL_MARKET_RESUME: "Resumed a market",
  PORTAL_MARKET_CANCEL: "Cancelled a market",
};

function when(at) {
  return new Date(at).toLocaleString("en-KE", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function useApprovalHistory({ marketId, pageSize = 50, reloadKey = 0 } = {}) {
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    apiGet(`/portal/approvals/history${buildQueryString({ marketId, page: 1, pageSize })}`)
      .then((res) => {
        if (!cancelled) setState({ items: res.items || [], total: res.total || 0, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ items: [], total: 0, loading: false, error: err.message || "Could not load the approval history." });
      });
    return () => {
      cancelled = true;
    };
  }, [marketId, pageSize, reloadKey]);
  return state;
}

export function ApprovalHistoryList({ state, showMarket = true, empty = "No approval decisions recorded yet." }) {
  if (state.error) return <p className="text-sm text-red-500">{state.error}</p>;
  if (state.loading) return <div className="h-24 animate-pulse rounded-md bg-accent" />;
  if (state.items.length === 0) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="text-xs text-gray-500">
            <th className="pb-2 pr-3 font-semibold">When</th>
            {showMarket && <th className="pb-2 pr-3 font-semibold">Market</th>}
            <th className="pb-2 pr-3 font-semibold">Decision</th>
            <th className="pb-2 pr-3 font-semibold">By</th>
            <th className="pb-2 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          {state.items.map((item) => {
            const meta = APPROVAL_ACTION_META[item.action] || { label: item.action, tone: "gray" };
            return (
              <tr key={item.id} className="border-t border-gray-100 align-top dark:border-white/5">
                <td className="whitespace-nowrap py-2 pr-3 text-xs text-gray-600 dark:text-gray-300">{when(item.at)}</td>
                {showMarket && (
                  <td className="max-w-[260px] py-2 pr-3 text-xs">
                    <Link to={`/portal/markets/${item.marketId}`} className="line-clamp-2 font-medium text-navy-700 hover:text-brand-500 dark:text-white">
                      {item.question}
                    </Link>
                  </td>
                )}
                <td className="py-2 pr-3">
                  <StatusBadge label={meta.label} tone={meta.tone} />
                </td>
                <td className="py-2 pr-3 text-xs text-navy-700 dark:text-gray-200">{item.actorIsYou ? "You" : item.actorLabel}</td>
                <td className="py-2 text-xs text-gray-600 dark:text-gray-300">{item.notes || "No notes"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function usePartnerReport(path, period, reloadKey = 0) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    const { from, to } = periodRange(period);
    setState((s) => ({ ...s, loading: true, error: null }));
    apiGet(`${path}${buildQueryString({ from, to })}`)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.message || "Could not load the report." });
      });
    return () => {
      cancelled = true;
    };
  }, [path, period, reloadKey]);
  return state;
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function PartnerReportView({ path, exportPath, period, onPeriodChange, title = "Performance report", description, compact = false }) {
  const { data, loading, error } = usePartnerReport(path, period);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const c = data?.currency || "KES";

  const runExport = useCallback(async () => {
    if (!exportPath) return;
    setExporting(true);
    setExportError(null);
    try {
      const { from, to } = periodRange(period);
      const res = await apiGet(`${exportPath}${buildQueryString({ from, to })}`);
      downloadCsv(res.filename, res.csv);
    } catch (err) {
      setExportError(err.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  }, [exportPath, period]);

  const t = data?.totals;
  return (
    <div className="flex flex-col gap-4">
      {!compact && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-navy-700 dark:text-white">{title}</p>
            {description && <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">{description}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {onPeriodChange && <PeriodPicker value={period} onChange={onPeriodChange} />}
            {exportPath && (
              <button
                type="button"
                onClick={runExport}
                disabled={exporting || !data}
                className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
              >
                {exporting ? "Exporting…" : "Export report (CSV)"}
              </button>
            )}
          </div>
        </div>
      )}
      {(error || exportError) && <div className="rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error || exportError}</div>}
      {loading && !data ? (
        <TileSkeleton count={compact ? 4 : 6} />
      ) : t ? (
        <div className={`grid grid-cols-2 gap-3 ${compact ? "lg:grid-cols-4" : "lg:grid-cols-3 2xl:grid-cols-6"}`}>
          <Tile label="Markets" value={t.marketCount.toLocaleString()} />
          <Tile label="Trades" value={t.tradeCount.toLocaleString()} hint="In this period" />
          <Tile label="Traded volume" value={formatKes(t.volume, c)} tone="blue" />
          <Tile label="Traders" value={t.traderCount.toLocaleString()} hint="People holding or who held a position" />
          {!compact && <Tile label="Net revenue" value={formatKes(t.split.net, c)} hint="Commission and settlement surplus" />}
          <Tile label={`Partner share (${bpsLabel(data.partner.shareBps)})`} value={formatKes(t.split.partner, c)} tone="green" emphasis />
        </div>
      ) : null}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="pb-2 pr-3 font-semibold">Market</th>
                <th className="pb-2 pr-3 font-semibold">Status</th>
                <th className="pb-2 pr-3 text-right font-semibold">Trades</th>
                <th className="pb-2 pr-3 text-right font-semibold">Volume</th>
                <th className="pb-2 pr-3 text-right font-semibold">Traders</th>
                <th className="pb-2 pr-3 text-right font-semibold">Net revenue</th>
                <th className="pb-2 text-right font-semibold">Partner share</th>
              </tr>
            </thead>
            <tbody>
              {data.markets.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-gray-500">
                    No markets yet.
                  </td>
                </tr>
              )}
              {data.markets.map((m) => (
                <tr key={m.marketId} className="border-t border-gray-100 align-top dark:border-white/5">
                  <td className="max-w-[280px] py-2 pr-3 text-xs">
                    <Link to={`/portal/markets/${m.marketId}`} className="line-clamp-2 font-medium text-navy-700 hover:text-brand-500 dark:text-white">
                      {m.question}
                    </Link>
                  </td>
                  <td className="py-2 pr-3">
                    <StatusBadge label={statusLabel(m.status)} tone={statusTone(m.status)} />
                  </td>
                  <td className="num py-2 pr-3 text-right text-sm text-navy-700 dark:text-gray-200">{m.tradeCount.toLocaleString()}</td>
                  <td className="num whitespace-nowrap py-2 pr-3 text-right text-sm text-navy-700 dark:text-gray-200">{formatKes(m.volume, c)}</td>
                  <td className="num py-2 pr-3 text-right text-sm text-navy-700 dark:text-gray-200">{m.traderCount.toLocaleString()}</td>
                  <td className="num whitespace-nowrap py-2 pr-3 text-right text-sm text-navy-700 dark:text-gray-200">{formatKes(m.split.net, c)}</td>
                  <td className="num whitespace-nowrap py-2 text-right text-sm font-semibold text-green-600 dark:text-green-400">{formatKes(m.split.partner, c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PartnerActivityLog({ partnerId }) {
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    apiGet(`/portal/partners/${partnerId}/activity${buildQueryString({ page: 1, pageSize: 25 })}`)
      .then((res) => {
        if (!cancelled) setState({ items: res.items || [], total: res.total || 0, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ items: [], total: 0, loading: false, error: err.message || "Could not load activity." });
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  if (state.error) return <p className="text-sm text-red-500">{state.error}</p>;
  if (state.loading) return <div className="h-16 animate-pulse rounded-md bg-accent" />;
  if (state.items.length === 0) return <p className="text-sm text-gray-500">No activity recorded for this partner yet.</p>;
  return (
    <ul className="divide-y divide-gray-100 dark:divide-white/5">
      {state.items.map((item) => (
        <li key={item.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-xs">
          <span className="text-navy-700 dark:text-white">
            <span className="font-semibold">{ACTIVITY_LABEL[item.action] || item.action.replaceAll("_", " ").toLowerCase()}</span>
            <span className="text-gray-500"> by {item.actorLabel}</span>
            {item.marketId && (
              <Link to={`/portal/markets/${item.marketId}`} className="ml-1 font-semibold text-brand-500">
                Open market
              </Link>
            )}
            {item.detail && <span className="block text-gray-500">{item.detail}</span>}
          </span>
          <time className="text-gray-400">{when(item.at)}</time>
        </li>
      ))}
      {state.total > state.items.length && <li className="py-2 text-xs text-gray-500">Showing the latest {state.items.length} of {state.total}.</li>}
    </ul>
  );
}

export function PartnerOversight({ partnerId }) {
  const [period, setPeriod] = useState("30d");
  return (
    <div className="mt-4 flex flex-col gap-4">
      <Card extra="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold text-navy-700 dark:text-white">This partner's markets and performance</p>
          <PeriodPicker value={period} onChange={setPeriod} />
        </div>
        <PartnerReportView path={`/portal/partners/${partnerId}/report`} period={period} compact />
      </Card>
      <Card extra="p-4">
        <p className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">Access and activity log</p>
        <PartnerActivityLog partnerId={partnerId} />
      </Card>
    </div>
  );
}
